import React, { useState, useEffect } from 'react';

import LocationPickerModal from './LocationPickerModal';
import CustomFormInput from './forms/CustomFormInput';
import { formatCurrency } from '../utils/currency';
import './TeachingLocationModal.css';

// Currency options matching backend support (9 currencies)
// ⚠️ ONLY 2-decimal currencies are offered. The money pipeline (storage write paths use
// `* 100`, and Stripe amounts are passed as major×100) assumes every currency has 2 decimal
// places. A 0-decimal currency (JPY) or 3-decimal (KWD) would be charged 100×/1000× wrong by
// Stripe. Do NOT add a non-2-decimal currency here until storage + Stripe amount construction
// are made decimal-aware. See PRP/00-research/ (multi-currency decimal support) + ROADMAP.
const CURRENCY_OPTIONS = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
    { code: 'NZD', symbol: '$', name: 'New Zealand Dollar' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar' },
];

/**
 * TeachingLocationModal - Wraps LocationPickerModal with Venue Hire Pricing
 *
 * Follows TravelPeriodModal pattern - wraps LocationPickerModal and adds venue hire fields:
 * - Location selection (via nested LocationPickerModal)
 * - Venue hire enabled checkbox
 * - Amount input
 * - Currency dropdown (independent of instructor hourly rate)
 * - Pricing type (hourly vs per_session)
 * - Description field
 * - Live pricing preview (1-hour lesson breakdown)
 *
 * Props:
 * - isOpen: boolean - Modal open state
 * - onClose: function - Close modal callback
 * - onSave: function(locationData) - Save location callback with venueHire object
 * - initialData: object | null - Location data for edit mode
 * - instructorHourlyRate: object - { amount, currency } for pricing preview
 */
const TeachingLocationModal = ({
    isOpen,
    onClose,
    onSave,
    initialData = null,
    instructorHourlyRate = { amount: 75, currency: 'USD' }
}) => {
    const [locationName, setLocationName] = useState('');
    const [locationDescription, setLocationDescription] = useState('');
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [showLocationPicker, setShowLocationPicker] = useState(false);

    // Venue hire state
    const [venueHireEnabled, setVenueHireEnabled] = useState(false);
    const [venueHireAmount, setVenueHireAmount] = useState('');
    const [venueHireCurrency, setVenueHireCurrency] = useState(instructorHourlyRate.currency || 'USD');
    const [venueHireType, setVenueHireType] = useState('hourly');

    // Travel location flag
    const [isTravel, setIsTravel] = useState(false);

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const isEditMode = initialData !== null;

    // Helper function to extract suburb from address
    const extractSuburb = (address) => {
        if (!address) return '';

        // Try to extract suburb from address string
        // Format: "Street, Suburb, State Postcode, Country"
        const parts = address.split(',').map(p => p.trim());
        if (parts.length >= 2) {
            // Return second part (usually suburb/locality)
            return parts[1];
        }
        return '';
    };

    // Helper function to parse location name from description
    // Description format: "Name (Suburb)" or just "Name"
    const parseLocationName = (description) => {
        const match = description?.match(/^(.+?)\s*\((.+?)\)$/);
        if (match) {
            return match[1]; // Return name part
        }
        return description || ''; // Return as-is if no parentheses
    };

    // Populate form when editing
    useEffect(() => {
        if (isOpen && initialData) {
            // Parse location name from description (format: "Name (Suburb)")
            const parsedName = parseLocationName(initialData.description);
            setLocationName(parsedName);

            // Set location description if present (separate from venue hire)
            setLocationDescription(initialData.locationDescription || '');

            setSelectedLocation({
                description: initialData.description,
                address: initialData.address || '',
                latitude: initialData.latitude,
                longitude: initialData.longitude,
            });

            // Populate venue hire fields if present
            if (initialData.venueHire) {
                setVenueHireEnabled(initialData.venueHire.enabled || false);
                setVenueHireAmount(initialData.venueHire.amount ? String(initialData.venueHire.amount) : '');
                setVenueHireCurrency(initialData.venueHire.currency || 'USD');
                setVenueHireType(initialData.venueHire.type || 'hourly');
            }

            // Travel location flag (legacy locations missing the field default to false)
            setIsTravel(initialData.isTravel === true);
        }
    }, [isOpen, initialData]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setLocationName('');
            setLocationDescription('');
            setSelectedLocation(null);
            setVenueHireEnabled(false);
            setVenueHireAmount('');
            setVenueHireCurrency('USD');
            setVenueHireType('hourly');
            setIsTravel(false);
            setErrors({});
        }
    }, [isOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!locationName || !locationName.trim()) {
            newErrors.locationName = 'Location name is required';
        }

        if (!selectedLocation) {
            newErrors.location = 'Location address is required';
        }

        if (venueHireEnabled) {
            if (!venueHireAmount || parseFloat(venueHireAmount) < 0) {
                newErrors.venueHireAmount = 'Valid amount is required when venue hire is enabled';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLocationSave = (locationData) => {
        setSelectedLocation(locationData);
        setShowLocationPicker(false);
        setErrors(prev => ({ ...prev, location: null }));
    };

    const handleSave = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (isLoading) return;
        if (!validateForm()) return;

        setIsLoading(true);

        try {
            // Extract suburb from selected location address
            const suburb = extractSuburb(selectedLocation.address);

            // Combine location name with suburb: "Studio 1 (Moorooka)"
            const combinedDescription = suburb
                ? `${locationName.trim()} (${suburb})`
                : locationName.trim();

            const locationData = {
                description: combinedDescription,
                locationDescription: locationDescription.trim() || undefined, // Optional location details
                address: selectedLocation.address,
                latitude: selectedLocation.latitude,
                longitude: selectedLocation.longitude,
                isTravel,
                venueHire: venueHireEnabled ? {
                    enabled: true,
                    amount: parseFloat(venueHireAmount),
                    currency: venueHireCurrency,
                    type: venueHireType,
                } : {
                    enabled: false,
                },
            };

            await onSave(locationData);
            onClose();
        } catch (error) {
            console.error('Error saving teaching location:', error);

            let errorMessage = 'Failed to save location. Please try again.';
            if (error.response?.data?.error?.message) {
                errorMessage = error.response.data.error.message;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setErrors({ submit: errorMessage });
            onClose();
            throw new Error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate pricing preview (1-hour lesson). instructorHourlyRate is passed from
    // the profile edit form's live value, which is in DOLLARS (the rate input is
    // dollar-denominated), same unit as the venue-hire config — so this preview works
    // entirely in dollars and displays via formatCurrency.
    const calculatePreview = () => {
        const artistFee = instructorHourlyRate.amount || 0;
        let venueHireFee = 0;

        if (venueHireEnabled && venueHireAmount) {
            const amount = parseFloat(venueHireAmount);

            // Convert to instructor currency if different
            // For preview, we'll just show "~" if currencies differ
            const currenciesDiffer = venueHireCurrency !== instructorHourlyRate.currency;

            if (venueHireType === 'hourly') {
                venueHireFee = amount; // 1 hour
            } else {
                venueHireFee = amount; // Flat per-session fee
            }

            return {
                artistFee,
                venueHireFee,
                total: artistFee + venueHireFee,
                currency: instructorHourlyRate.currency,
                showEstimate: currenciesDiffer,
                venueHireCurrency,
            };
        }

        return {
            artistFee,
            venueHireFee: 0,
            total: artistFee,
            currency: instructorHourlyRate.currency,
            showEstimate: false,
        };
    };

    const preview = calculatePreview();

    if (!isOpen) return null;

    return (
        <div className="teaching-location-modal-overlay" onClick={onClose}>
            <div className="teaching-location-modal" onClick={(e) => e.stopPropagation()}>
                <div className="teaching-location-modal-header">
                    <h2>{isEditMode ? 'Edit Teaching Location' : 'Add Teaching Location'}</h2>
                    <button type="button" className="teaching-location-modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSave}>
                    <div className="teaching-location-modal-body">
                        {errors.submit && (
                            <div className="error-message" style={{ marginBottom: '16px' }}>
                                {errors.submit}
                            </div>
                        )}

                        {/* Location Name */}
                        <div className="form-group">
                            <label htmlFor="locationName">
                                Location Name <span className="required">*</span>
                            </label>
                            <p className="form-hint">
                                Give this location a nickname (e.g., "Studio 1", "Home Studio"). The suburb will be shown alongside the name.
                            </p>
                            <input
                                type="text"
                                id="locationName"
                                className="form-input"
                                value={locationName}
                                onChange={(e) => {
                                    setLocationName(e.target.value);
                                    setErrors(prev => ({ ...prev, locationName: null }));
                                }}
                                placeholder="e.g., Studio 1, Home Studio, Main Room"
                                maxLength={50}
                                required
                            />
                            {errors.locationName && (
                                <span className="field-error">{errors.locationName}</span>
                            )}
                        </div>

                        {/* Location Address Selection */}
                        <div className="form-group">
                            <label htmlFor="location">
                                Location Address <span className="required">*</span>
                            </label>
                            {selectedLocation ? (
                                <div className="selected-location-display">
                                    <div className="location-display-content">
                                        <div className="location-display-description">
                                            {typeof selectedLocation.description === 'string'
                                                ? selectedLocation.description
                                                : JSON.stringify(selectedLocation.description)}
                                        </div>
                                        {selectedLocation.address && typeof selectedLocation.address === 'string' && (
                                            <div className="location-display-address">{selectedLocation.address}</div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowLocationPicker(true)}
                                        className="btn-change-location"
                                    >
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowLocationPicker(true)}
                                    className="btn-select-location"
                                >
                                    + Select Teaching Location
                                </button>
                            )}
                            {errors.location && (
                                <span className="field-error">{errors.location}</span>
                            )}
                        </div>

                        {/* Location Description */}
                        <div className="form-group">
                            <label htmlFor="locationDescription">
                                Location Description (Optional)
                            </label>
                            <p className="form-hint">
                                Add helpful details about accessing this location (e.g., hints for access, or codes for entry).
                            </p>
                            <input
                                type="text"
                                id="locationDescription"
                                className="form-input"
                                value={locationDescription}
                                onChange={(e) => setLocationDescription(e.target.value)}
                                placeholder="First floor, use 611 pin code"
                                maxLength={200}
                            />
                        </div>

                        {/* Travel Location Flag */}
                        <div className="form-group">
                            <CustomFormInput
                                type="checkbox"
                                variant="turquoise"
                                label="This is a travel location"
                                checked={isTravel}
                                onChange={(e) => setIsTravel(e.target.checked)}
                                hint="Travel locations are only shown in the booking dropdown when you create bookings inside one of your travel periods. Home locations are hidden during travel, and vice versa."
                            />
                        </div>

                        {/* Venue Hire Section */}
                        <div className="venue-hire-section">
                            <div className="venue-hire-header">
                                <CustomFormInput
                                    type="checkbox"
                                    variant="turquoise"
                                    label="Charge venue hire fee"
                                    checked={venueHireEnabled}
                                    onChange={(e) => setVenueHireEnabled(e.target.checked)}
                                    hint="Additional fee for studio or venue rental, charged on top of your lesson fee."
                                />
                            </div>

                            {venueHireEnabled && (
                                <div className="venue-hire-fields">
                                    <div className="form-row">
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label htmlFor="venueHireAmount">
                                                Amount <span className="required">*</span>
                                            </label>
                                            <div className="input-with-prefix">
                                                <span className="prefix">{CURRENCY_OPTIONS.find(c => c.code === venueHireCurrency)?.symbol || '$'}</span>
                                                <input
                                                    type="number"
                                                    id="venueHireAmount"
                                                    value={venueHireAmount}
                                                    onChange={(e) => {
                                                        setVenueHireAmount(e.target.value);
                                                        setErrors(prev => ({ ...prev, venueHireAmount: null }));
                                                    }}
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="1"
                                                    required={venueHireEnabled}
                                                />
                                            </div>
                                            {errors.venueHireAmount && (
                                                <span className="field-error">{errors.venueHireAmount}</span>
                                            )}
                                        </div>

                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label htmlFor="venueHireCurrency">Currency</label>
                                            <select
                                                id="venueHireCurrency"
                                                className="form-select"
                                                value={venueHireCurrency}
                                                onChange={(e) => setVenueHireCurrency(e.target.value)}
                                            >
                                                {CURRENCY_OPTIONS.map(currency => (
                                                    <option key={currency.code} value={currency.code}>
                                                        {currency.code} - {currency.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="venueHireType">Pricing Type</label>
                                        <select
                                            id="venueHireType"
                                            className="form-select"
                                            value={venueHireType}
                                            onChange={(e) => setVenueHireType(e.target.value)}
                                        >
                                            <option value="hourly">Per Hour</option>
                                            <option value="per_session">Per Session (Flat Fee)</option>
                                        </select>
                                        <p className="form-hint">
                                            {venueHireType === 'hourly'
                                                ? 'Charged per hour of lesson duration'
                                                : 'Charged once per lesson regardless of duration'
                                            }
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Pricing Preview — temporarily suppressed (not showing currency correctly) */}
                        {/* {venueHireEnabled && venueHireAmount && (
                            <div className="pricing-preview">
                                <h3>Pricing Preview (1-hour lesson)</h3>
                                <div className="preview-row">
                                    <span>Artist Fee</span>
                                    <span>{formatCurrency(preview.artistFee, preview.currency)}</span>
                                </div>
                                <div className="preview-row">
                                    <span>Venue Hire {preview.showEstimate && `(${venueHireCurrency})`}</span>
                                    <span>
                                        {preview.showEstimate && '~'}
                                        {formatCurrency(preview.venueHireFee, preview.currency)}
                                    </span>
                                </div>
                                <div className="preview-row preview-total">
                                    <span>Total</span>
                                    <span>
                                        {preview.showEstimate && '~'}
                                        {formatCurrency(preview.total, preview.currency)}
                                    </span>
                                </div>
                                {preview.showEstimate && (
                                    <p className="preview-note">
                                        ~ Estimated conversion - actual rate determined at booking time
                                    </p>
                                )}
                            </div>
                        )} */}
                    </div>

                    <div className="teaching-location-modal-footer">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Saving...' : (isEditMode ? 'Update Location' : 'Save Location')}
                        </button>
                    </div>
                </form>
            </div>

            {/* Nested Location Picker Modal */}
            <LocationPickerModal
                isOpen={showLocationPicker}
                onClose={() => setShowLocationPicker(false)}
                onSave={handleLocationSave}
                currentLocation={selectedLocation}
                mode="teaching"
                title="Select Teaching Location"
            />
        </div>
    );
};

export default TeachingLocationModal;
